import { useEffect, useRef, useState } from "react";
import type { ReactNode } from "react";
import {
  ActivityIndicator,
  Platform,
  Pressable,
  ScrollView,
  TextInput,
  View,
} from "react-native";
import { MapPin, type LucideIcon } from "lucide-react-native";
import {
  getPlaceDetails,
  searchPlaces,
  type PlacePrediction,
} from "@src/packages/clinics/utils/googlePlaces";
import { useDebounce } from "@src/packages/hooks/useDebounce";
import { colors, typography } from "../theme/tokens";
import { MoonFormField } from "./MoonFormField";
import { MoonText } from "./MoonText";
import { TextField } from "./TextField";
import {
  extractAddressFields,
  freeTextAddress,
  type AddressData,
} from "./addressFields";
import {
  pressLabel,
  usePressCapture,
} from "@src/packages/analytics/usePressCapture";

/**
 * Inline address field with Google Places autocomplete dropdown.
 *
 * Mirrors the `MoonPhoneField` pattern: a single TextInput with a
 * suggestion list that drops down below as the user types. No modal,
 * no second tap to save — pick a prediction and the parent receives a
 * structured `AddressData` payload built from the resolved place details.
 *
 * The field is controlled: the on-screen text is always `value`, so the
 * caller decides what it shows — the saved address straight from the
 * patient query, or the draft being edited. Free text is reported as
 * `address1` on every keystroke, so callers can save it without requiring
 * a prediction or a blur event. The other structured fields are null in
 * that fallback; a selected prediction reports the resolved place details.
 */
export type { AddressData } from "./addressFields";

type Props = {
  label: string;
  /** Decorative Lucide icon rendered immediately before the field label. */
  labelIcon?: LucideIcon;
  /** Short supporting copy rendered directly beneath the field label. */
  labelHint?: string;
  /**
   * Optional icon inside the input, before the address text. Defaults to a
   * map pin; pass `null` when the label icon already provides that context.
   */
  icon?: ReactNode;
  /** The text shown in the field (e.g. "123 Main St, NYC, USA"). */
  value: string | null;
  onChange: (data: AddressData) => void;
  /**
   * Shows a clear control inside the box while there is text. Clearing
   * reports an empty address with no country, so a caller that keeps the
   * country on its own keeps it.
   */
  onClear?: () => void;
  placeholder?: string;
  error?: string;
  hint?: string;
  required?: boolean;
  disabled?: boolean;
  loading?: boolean;
  testID?: string;
};

export function AddressField({
  label,
  labelIcon,
  labelHint,
  icon,
  value,
  onChange,
  onClear,
  placeholder = "Start typing your address",
  error,
  hint,
  required,
  disabled,
  loading,
  testID,
}: Props) {
  const capturePress = usePressCapture("AddressField");
  const query = value ?? "";
  const [predictions, setPredictions] = useState<PlacePrediction[]>([]);
  const [open, setOpen] = useState(false);
  const [resolving, setResolving] = useState(false);
  const inputRef = useRef<TextInput | null>(null);
  // Only text the user typed is searched. `value` also changes when the
  // caller shows a saved address or a resolved prediction, and neither
  // should pop the dropdown open.
  const typedRef = useRef(false);
  const debouncedQuery = useDebounce(query, 300);

  useEffect(() => {
    const trimmed = debouncedQuery.trim();
    if (!typedRef.current || trimmed.length < 3) {
      setPredictions([]);
      return;
    }
    let cancelled = false;
    searchPlaces(trimmed, { addressesOnly: true })
      .then((results) => {
        if (cancelled) return;
        setPredictions(results);
        if (results.length > 0) setOpen(true);
      })
      .catch((err) => {
        console.error("[AddressField] searchPlaces failed:", err);
        if (!cancelled) setPredictions([]);
      });
    return () => {
      cancelled = true;
    };
  }, [debouncedQuery]);

  const freeText = freeTextAddress;

  const handleSelect = async (prediction: PlacePrediction) => {
    typedRef.current = false;
    setOpen(false);
    setPredictions([]);
    inputRef.current?.blur();
    // Show the prediction right away; the structured details replace it
    // once they resolve.
    onChange(freeText(prediction.description));
    setResolving(true);
    try {
      const details = await getPlaceDetails(prediction.placeId);
      onChange(
        details
          ? extractAddressFields(details)
          : freeText(prediction.description),
      );
    } catch (err) {
      console.error("[AddressField] getPlaceDetails failed:", err);
      onChange(freeText(prediction.description));
    } finally {
      setResolving(false);
    }
  };

  const handleBlur = () => {
    // Slight delay so a tap on a prediction row fires its onPress before
    // the dropdown closes.
    setTimeout(() => setOpen(false), 150);
  };

  return (
    <MoonFormField
      label={label}
      labelIcon={labelIcon}
      labelHint={labelHint}
      error={error}
      hint={hint}
      required={required}
      disabled={disabled}
      loading={loading}
      testID={testID}
    >
      <View>
        <TextField
          ref={inputRef}
          value={query}
          onChangeText={(text) => {
            typedRef.current = true;
            onChange(freeText(text || null));
            if (text.trim().length >= 3) setOpen(true);
          }}
          onFocus={() => {
            if (predictions.length > 0) setOpen(true);
          }}
          onBlur={handleBlur}
          placeholder={placeholder}
          hasError={!!error}
          disabled={disabled}
          // The labelled field around this one already dims while disabled.
          dimWhenDisabled={false}
          accessibilityLabel={label}
          autoCorrect={false}
          spellCheck={false}
          // No `textContentType` — it triggers the browser's native autofill
          // on web, which renders its own dropdown on top of ours and races
          // onChangeText with the autofilled value.
          autoComplete="off"
          leading={
            icon === undefined ? (
              <MapPin size={13} color={colors.textLight} strokeWidth={1.5} />
            ) : (
              (icon ?? undefined)
            )
          }
          trailing={
            resolving ? (
              <ActivityIndicator size="small" color={colors.warm} />
            ) : undefined
          }
          onClear={
            onClear
              ? () => {
                  typedRef.current = false;
                  setOpen(false);
                  setPredictions([]);
                  onClear();
                }
              : undefined
          }
        />

        {open && predictions.length > 0 && !disabled ? (
          <View
            style={{
              marginTop: 6,
              borderRadius: 10,
              borderWidth: 1,
              borderColor: "rgba(0,0,0,0.12)",
              backgroundColor: "#FFFFFF",
              overflow: "hidden",
              maxHeight: 280,
              ...Platform.select({
                ios: {
                  shadowColor: "#000",
                  shadowOffset: { width: 0, height: 4 },
                  shadowOpacity: 0.08,
                  shadowRadius: 12,
                },
                android: { elevation: 6 },
                web: { boxShadow: "0 4px 16px rgba(0,0,0,0.08)" },
              }),
            }}
          >
            <ScrollView
              keyboardShouldPersistTaps="handled"
              nestedScrollEnabled
              showsVerticalScrollIndicator={false}
            >
              {predictions.map((prediction) => (
                <Pressable
                  key={prediction.placeId}
                  onPress={() => {
                    capturePress(
                      pressLabel(prediction.description),
                      "address_suggestion",
                    );
                    void handleSelect(prediction);
                  }}
                >
                  {({ pressed }) => (
                    <View
                      style={{
                        flexDirection: "row",
                        alignItems: "center",
                        paddingHorizontal: 12,
                        paddingVertical: 10,
                        backgroundColor: pressed
                          ? "rgba(0,0,0,0.04)"
                          : "transparent",
                      }}
                    >
                      <MapPin
                        size={13}
                        color={colors.textFaint}
                        strokeWidth={1.5}
                      />
                      <View style={{ flex: 1, marginLeft: 10 }}>
                        <MoonText
                          style={{
                            fontFamily: typography.families.sansMedium,
                            fontSize: 13,
                            color: colors.text,
                            fontWeight: "500",
                          }}
                          numberOfLines={1}
                        >
                          {prediction.structuredFormatting.mainText}
                        </MoonText>
                        <MoonText
                          style={{
                            fontFamily: typography.families.sans,
                            fontSize: 12,
                            color: colors.textFaint,
                            marginTop: 2,
                          }}
                          numberOfLines={1}
                        >
                          {prediction.structuredFormatting.secondaryText}
                        </MoonText>
                      </View>
                    </View>
                  )}
                </Pressable>
              ))}
            </ScrollView>
          </View>
        ) : null}
      </View>
    </MoonFormField>
  );
}
